/**
 * Everything the home dashboard can show, described once.
 *
 * The dashboard, the Settings › Dashboard page and the tests all read this
 * list, so a widget added by a module built later is one entry here: it
 * appears on the dashboard, becomes switchable in Settings, and needs no
 * change to anyone's saved layout.
 */

export type WidgetKind = 'card' | 'section';

/** Where a section sits: the wide main column, or the narrower one beside it. */
export type WidgetColumn = 'main' | 'side';

export interface WidgetDef {
  id: string;
  kind: WidgetKind;
  /** Shown in Settings › Dashboard. */
  label: string;
  /** One line in Settings saying what it shows. */
  hint: string;
  /** The permission that reveals it; '*' means every signed-in user. */
  permission: string;
  /**
   * The part of `GET dashboard/overview` it draws. When the server leaves that
   * part out — because the caller may not see it — the widget is not shown,
   * whatever the person's settings say.
   */
  needs?: 'stock' | 'farms' | 'people';
  /** Sections only. */
  column?: WidgetColumn;
}

export const CARDS: WidgetDef[] = [
  {
    id: 'stock-value',
    kind: 'card',
    label: 'Stock value',
    hint: 'What the stock on hand is worth. Shows the number of items instead if you cannot see costs.',
    permission: 'inv.dashboard.read',
    needs: 'stock',
  },
  {
    id: 'low-stock',
    kind: 'card',
    label: 'Running short',
    hint: 'Items below the level you set for reordering.',
    permission: 'inv.dashboard.read',
    needs: 'stock',
  },
  {
    id: 'expiring',
    kind: 'card',
    label: 'Expiring soon',
    hint: 'Batches close to their expiry date.',
    permission: 'inv.dashboard.read',
    needs: 'stock',
  },
  {
    id: 'farms',
    kind: 'card',
    label: 'Farms',
    hint: 'Farms and sheds you can see, and the bird capacity.',
    permission: 'mdm.farm.read',
    needs: 'farms',
  },
  {
    id: 'staff',
    kind: 'card',
    label: 'People',
    hint: 'How many work here: monthly and daily wage.',
    permission: 'hr.dashboard.read',
    needs: 'people',
  },
  {
    id: 'present',
    kind: 'card',
    label: 'Present today',
    hint: "How many are marked present on today's register.",
    permission: 'hr.dashboard.read',
    needs: 'people',
  },
  {
    id: 'waiting',
    kind: 'card',
    label: 'Waiting for you',
    hint: 'How many things are waiting for your decision.',
    permission: '*',
  },
];

export const SECTIONS: WidgetDef[] = [
  {
    id: 'announcements',
    kind: 'section',
    label: 'Announcements',
    // Everyone sees the notices addressed to them; only the people who write
    // them get the New and Manage buttons, which the board itself decides.
    hint: 'Notices put up for you, the most pressing one first and largest.',
    permission: '*',
    column: 'main',
  },
  {
    id: 'action-needed',
    kind: 'section',
    label: 'Action needed',
    hint: 'Everything waiting for you, with a button to open it.',
    permission: '*',
    column: 'side',
  },
  {
    id: 'coming-up',
    kind: 'section',
    label: 'Coming up',
    hint: 'The next holidays, and your reminders for the week.',
    permission: '*',
    column: 'side',
  },
];

export const ALL_WIDGETS = [...CARDS, ...SECTIONS];

export function widgetsOfKind(kind: WidgetKind): WidgetDef[] {
  return kind === 'card' ? CARDS : SECTIONS;
}
